import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { subscriptionsApi } from '../../services/api';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material';
import { X, CreditCard, ArrowUpRight } from 'lucide-react';

const SubscriptionStatus = ({ token, onError }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      try {
        setLoading(true);
        const result = await subscriptionsApi.getStatus(token);
        setStatus(result);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
        if (onError) onError(error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      loadSubscriptionStatus();
    }
  }, [token, onError]);

  const handleOpenDetails = () => {
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  const handleRequestUpgrade = async () => {
    if (!token) return;

    try {
      setUpgradeLoading(true);
      setUpgradeMessage('');

      await subscriptionsApi.requestUpgrade(null, null, token);

      setUpgradeMessage(t('subscription.upgrade_request_success', 'Your upgrade request has been sent. We will contact you soon.'));

      // Clear the message after 5 seconds
      setTimeout(() => {
        setUpgradeMessage('');
      }, 5000);
    } catch (error) {
      console.error('Error requesting subscription upgrade:', error);
      setUpgradeMessage(t('subscription.upgrade_request_error', 'Failed to send upgrade request. Please try again.'));
      if (onError) onError(error);
    } finally {
      setUpgradeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 animate-pulse">
        <CreditCard className="w-5 h-5 text-gray-400" />
        <div className="h-4 bg-gray-200 rounded w-24"></div>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-purple-500" />
        <button 
          onClick={handleOpenDetails}
          className="text-blue-600 hover:text-blue-800 hover:underline"
        >
          {status.has_subscription 
            ? t('subscription.status.active_plan', { plan: status.plan_name }) 
            : t('subscription.status.free_plan')}
        </button>
      </div>

      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-center justify-between">
          <div>{t('terms_of_service.subscriptions.title')}</div>
          <IconButton onClick={handleCloseDetails} size="small">
            <X className="w-5 h-5" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                {status.has_subscription 
                  ? t('subscription.status.active_plan', { plan: status.plan_name }) 
                  : t('subscription.status.free_plan')}
              </h3>

              {status.has_subscription && status.subscription_active && (
                <p className="text-gray-600 text-sm mb-3">
                  {t('subscription.status.days_remaining', { days: status.days_remaining })}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <h4 className="text-md font-medium">{t('subscription.status.resource_limits')}</h4>

              <div className="grid grid-cols-2 gap-4">
                <ResourceLimit 
                  label={t('subscription.resources.sandboxes')}
                  used={status.limits.sandboxes.used}
                  max={status.limits.sandboxes.max}
                />

                <ResourceLimit 
                  label={t('subscription.resources.components')}
                  used={status.limits.components ? status.limits.components.used : "--"}
                  max={status.limits.components ? (status.limits.components.max > 0 ? status.limits.components.max : t('subscription.unlimited')) : (status.plan_type === 'free' ? '15' : t('subscription.unlimited'))}
                />

                <ResourceLimit 
                  label={t('subscription.resources.tutors')}
                  used={status.limits.tutors.used}
                  max={status.limits.tutors.max}
                />

                <ResourceLimit 
                  label={t('subscription.resources.templates')}
                  used={status.limits.templates.used}
                  max={status.limits.templates.max}
                />

                <ResourceLimit 
                  label={t('subscription.resources.repository_size')}
                  used={status.limits.repository_size ? status.limits.repository_size.used + ' MB' : "--"}
                  max={status.limits.repository_size ? (status.limits.repository_size.max > 0 ? status.limits.repository_size.max + ' MB' : t('subscription.unlimited')) : (status.plan_type === 'free' ? '5 MB' : t('subscription.unlimited'))}
                />

                <ResourceLimit 
                  label={t('subscription.resources.api_requests')}
                  used={status.limits.api_requests ? status.limits.api_requests.used : "--"}
                  max={status.limits.api_requests ? (status.limits.api_requests.max > 0 ? status.limits.api_requests.max + '/day' : t('subscription.unlimited')) : (status.plan_type === 'free' ? '100/day' : t('subscription.unlimited'))}
                />
              </div>
            </div>

            {!status.has_subscription && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex flex-col space-y-3">
                  <p className="text-gray-700">
                    {t('subscription.upgrade')}
                  </p>
                  <button
                    onClick={handleRequestUpgrade}
                    disabled={upgradeLoading}
                    className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {upgradeLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('subscription.requesting_upgrade', 'Requesting...')}
                      </span>
                    ) : (
                      <span className="flex items-center">
                        {t('subscription.request_upgrade', 'Request Upgrade')}
                        <ArrowUpRight className="ml-1 w-4 h-4" />
                      </span>
                    )}
                  </button>
                  {upgradeMessage && (
                    <div className={`text-sm p-2 rounded ${upgradeMessage.includes('error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {upgradeMessage}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDetails} color="primary">
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ResourceLimit = ({ label, used, max }) => {
  const percentage = typeof used === 'number' && typeof max === 'number' 
    ? Math.min(100, Math.round((used / max) * 100)) 
    : null;

  return (
    <div className="text-sm">
      <div className="flex justify-between text-gray-700 mb-1">
        <span>{label}</span>
        <span>
          {used} / {max}
        </span>
      </div>

      {percentage !== null && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full ${
              percentage > 90 ? 'bg-red-500' : 
              percentage > 70 ? 'bg-yellow-500' : 
              'bg-green-500'
            }`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionStatus;
